/**
 * Build-a-Bundle Alpine.js Component
 * Integrates with Skio Static Bundle (Classic Box V2)
 */

document.addEventListener('alpine:init', () => {
  Alpine.data('bundleBuilder', (config) => ({
    // Config from Liquid
    bundleVariantId: config.bundleVariantId,
    sellingPlanId: config.sellingPlanId,
    availableVariantIds: config.availableVariantIds || [],
    text: config.text || {},

    // Data from Skio API
    products: [],
    bundleSize: 6,
    oneTimePrice: 149.95,
    subscriptionPrice: 119.96,
    subscriptionDiscount: 20,

    // User state
    selectedItems: [],
    isSubscription: true,
    mobileDrawerOpen: false,
    activeCategory: 'all',

    // UI state
    isLoading: true,
    isAddingToCart: false,
    error: null,
    stickyBarVisible: false,

    /**
     * Initialize - fetch bundle data from Skio
     */
    async init() {
      console.log('[Bundle Builder] Initializing with config:', {
        bundleVariantId: this.bundleVariantId,
        sellingPlanId: this.sellingPlanId,
        availableVariantIds: this.availableVariantIds.length + ' variants',
        firstFewIds: this.availableVariantIds.slice(0, 5)
      });

      try {
        await this.fetchBundleData();
        // Setup scroll listener for sticky bar visibility
        this.setupScrollListener();
      } catch (e) {
        console.error('[Bundle Builder] Init error:', e);
        this.error = 'Failed to load bundle products. Please refresh the page.';
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Setup scroll listener to show/hide sticky bar
     */
    setupScrollListener() {
      const productsSection = document.getElementById('bundle-products');
      if (!productsSection) return;

      const checkVisibility = () => {
        const rect = productsSection.getBoundingClientRect();
        // Show sticky bar when products section is in view (top of section is above viewport center)
        this.stickyBarVisible = rect.top < window.innerHeight * 0.7;
      };

      // Initial check
      checkVisibility();

      // Listen for scroll
      window.addEventListener('scroll', checkVisibility, { passive: true });
    },

    /**
     * Fetch bundle configuration from Skio API
     */
    async fetchBundleData() {
      const response = await fetch('https://api.skio.com/storefront-http/get-classic-box-v2', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productVariantPlatformId: this.bundleVariantId,
          sellingPlanPlatformId: this.sellingPlanId || undefined
        })
      });

      const data = await response.json();
      console.log('[Bundle Builder] Skio API response:', data);

      if (!data.ok) {
        throw new Error('Skio API returned error');
      }

      const box = data.ClassicBoxV2;

      // Set bundle config from API
      this.bundleSize = box.size || 6;
      
      // Skio returns price already in dollars (e.g., 119.96)
      // This is the subscription price when a selling plan is passed
      this.subscriptionPrice = box.price || 119.96;
      
      // Calculate one-time price from subscription price and discount
      // If 20% off gives us $119.96, then full price is $119.96 / 0.8 = $149.95
      this.subscriptionDiscount = 20; // Default 20% off
      this.oneTimePrice = this.subscriptionPrice / (1 - this.subscriptionDiscount / 100);
      
      console.log('[Bundle Builder] Prices:', {
        subscriptionPrice: this.subscriptionPrice,
        oneTimePrice: this.oneTimePrice,
        discount: this.subscriptionDiscount + '%'
      });

      // Log the variant IDs from Skio for debugging
      console.log('[Bundle Builder] Skio variant IDs:', 
        box.selectableProductVariants.map(v => ({
          title: v.productTitle,
          platformId: v.platformId,
          platformIdType: typeof v.platformId,
          price: v.price
        }))
      );

      // TEMPORARY: Skip availability filter to see products
      // TODO: Fix variant ID matching and re-enable filter
      const skipAvailabilityFilter = true;

      if (skipAvailabilityFilter) {
        console.log('[Bundle Builder] SKIPPING availability filter (temporary)');
        this.products = box.selectableProductVariants;
      } else {
        // Filter products by availability
        this.products = box.selectableProductVariants.filter(variant => {
          // Try both string and number comparison
          const variantIdNum = parseInt(variant.platformId, 10);
          const variantIdStr = String(variant.platformId);
          
          const isAvailableNum = this.availableVariantIds.includes(variantIdNum);
          const isAvailableStr = this.availableVariantIds.includes(variantIdStr);
          const isAvailable = isAvailableNum || isAvailableStr;
          
          if (!isAvailable) {
            console.log('[Bundle Builder] Filtering out unavailable variant:', variant.productTitle, 'ID:', variant.platformId);
          }
          
          return isAvailable;
        });
      }

      // Add placeholder review data (will be replaced with real metafield data)
      this.products = this.products.map(p => ({
        ...p,
        rating: 5,
        reviewCount: 1762
      }));

      console.log('[Bundle Builder] Loaded', this.products.length, 'products');
    },

    /**
     * Computed: Total quantity of items in bundle
     */
    get totalQuantity() {
      return this.selectedItems.reduce((sum, item) => sum + item.quantity, 0);
    },

    /**
     * Computed: Remaining slots in bundle
     */
    get remainingSlots() {
      return this.bundleSize - this.totalQuantity;
    },

    /**
     * Computed: Is bundle complete?
     */
    get isComplete() {
      return this.totalQuantity >= this.bundleSize;
    },

    /**
     * Computed: Progress percentage (0-100) for progress bar
     */
    get progressPercentage() {
      return Math.min((this.totalQuantity / this.bundleSize) * 100, 100);
    },

    /**
     * Computed: Products filtered by the active category pill
     */
    get filteredProducts() {
      if (this.activeCategory === 'all') return this.products;
      return this.products.filter(p => this.getProductCategory(p) === this.activeCategory);
    },

    /**
     * Computed: Display price based on subscription toggle
     */
    get displayPrice() {
      if (this.isSubscription) {
        return this.subscriptionPrice.toFixed(2);
      }
      return this.oneTimePrice.toFixed(2);
    },

    /**
     * Computed: Total original price of all items (before any discounts)
     */
    get totalOriginalPrice() {
      let total = 0;
      this.progressSlots.forEach((slot, index) => {
        if (slot !== null) {
          total += parseFloat(slot.originalPrice);
        }
      });
      return total.toFixed(2);
    },

    /**
     * Computed: Total savings
     */
    get totalSavings() {
      const original = parseFloat(this.totalOriginalPrice);
      const current = parseFloat(this.displayPrice);
      return (original - current).toFixed(2);
    },

    /**
     * Computed: Array of progress slots for display (6 slots, filled or empty)
     * Each filled slot includes price information
     */
    get progressSlots() {
      const slots = [];

      // Add filled slots with price info
      this.selectedItems.forEach(item => {
        const product = this.products.find(p => p.platformId === item.variantId);
        const originalPrice = product ? product.price : 0;
        const discountedPrice = this.isSubscription 
          ? originalPrice * (1 - this.subscriptionDiscount / 100)
          : originalPrice;

        for (let i = 0; i < item.quantity; i++) {
          slots.push({
            variantId: item.variantId,
            productTitle: item.productTitle,
            imageSrc: item.imageSrc,
            originalPrice: originalPrice.toFixed(2),
            displayPrice: discountedPrice.toFixed(2)
          });
        }
      });

      // Pad with empty slots
      while (slots.length < this.bundleSize) {
        slots.push(null);
      }

      return slots;
    },

    /**
     * Legacy: Array of slots for backward compatibility
     */
    get slots() {
      return this.progressSlots;
    },

    /**
     * Get the original (non-discounted) price for a product
     * @param {Object} product - Product object from Skio API
     * @returns {string} - Formatted price string (e.g., "29.99")
     */
    getOriginalPrice(product) {
      const price = product.price || 0;
      return price.toFixed(2);
    },

    /**
     * Get the display price for a product based on subscription status
     * @param {Object} product - Product object from Skio API
     * @returns {string} - Formatted price string (e.g., "23.99")
     */
    getDisplayPrice(product) {
      const price = product.price || 0;
      
      if (this.isSubscription) {
        // Apply subscription discount
        const discountedPrice = price * (1 - this.subscriptionDiscount / 100);
        return discountedPrice.toFixed(2);
      }
      
      return price.toFixed(2);
    },

    /**
     * Derive the filter-pill category from a product's title
     * (Skio doesn't expose a category/tag field, so we match the flavor-family
     * naming convention every product title follows, e.g. "Sour Gummy Wormz")
     * @param {Object} product - Product object from Skio API
     * @returns {string} - 'wormz' | 'cruncheez' | 'tiniez' | 'chewerz' | 'other'
     */
    getProductCategory(product) {
      const title = (product.productTitle || '').toLowerCase();
      if (title.includes('wormz')) return 'wormz';
      if (title.includes('cruncheez')) return 'cruncheez';
      if (title.includes('tiniez')) return 'tiniez';
      if (title.includes('chewerz')) return 'chewerz';
      return 'other';
    },

    /**
     * Get quantity of a specific product in the bundle
     */
    getItemQuantity(variantId) {
      const item = this.selectedItems.find(i => i.variantId === variantId);
      return item ? item.quantity : 0;
    },

    /**
     * Add item to bundle
     */
    addItem(product) {
      if (this.isComplete) {
        console.log('[Bundle Builder] Bundle is full');
        return;
      }

      const existing = this.selectedItems.find(i => i.variantId === product.platformId);

      if (existing) {
        existing.quantity++;
      } else {
        this.selectedItems.push({
          variantId: product.platformId,
          productTitle: product.productTitle,
          imageSrc: product.imageSrc,
          quantity: 1
        });
      }

      console.log('[Bundle Builder] Added item:', product.productTitle, '| Total:', this.totalQuantity);

      // Auto-open mobile drawer when complete
      if (this.isComplete && window.innerWidth < 1024) {
        this.mobileDrawerOpen = true;
      }
    },

    /**
     * Remove one quantity of item from bundle by variant ID
     */
    removeItem(variantId) {
      const index = this.selectedItems.findIndex(i => i.variantId === variantId);
      if (index === -1) return;

      if (this.selectedItems[index].quantity > 1) {
        this.selectedItems[index].quantity--;
      } else {
        this.selectedItems.splice(index, 1);
      }

      console.log('[Bundle Builder] Removed item | Total:', this.totalQuantity);
    },

    /**
     * Remove item from bundle by slot index
     * This is used by the progress track UI
     */
    removeItemByIndex(slotIndex) {
      // Find which item and quantity position this slot represents
      let currentIndex = 0;
      
      for (let i = 0; i < this.selectedItems.length; i++) {
        const item = this.selectedItems[i];
        const itemEndIndex = currentIndex + item.quantity;
        
        if (slotIndex >= currentIndex && slotIndex < itemEndIndex) {
          // Found the item - decrease quantity or remove
          if (item.quantity > 1) {
            item.quantity--;
          } else {
            this.selectedItems.splice(i, 1);
          }
          console.log('[Bundle Builder] Removed item at slot', slotIndex, '| Total:', this.totalQuantity);
          return;
        }
        
        currentIndex = itemEndIndex;
      }
    },

    /**
     * Toggle mobile drawer
     */
    toggleDrawer() {
      this.mobileDrawerOpen = !this.mobileDrawerOpen;
    },

    /**
     * Add bundle to cart
     */
    async addToCart() {
      if (!this.isComplete) {
        console.log('[Bundle Builder] Bundle not complete');
        return;
      }

      this.isAddingToCart = true;

      // Build properties object for Skio
      const properties = {
        '_isClassicBoxV2': 'true'
      };

      this.selectedItems.forEach(item => {
        // Human-readable product name
        properties[item.productTitle] = item.quantity.toString();
        // Skio variant GID format - variantId is already in gid:// format
        // Property key format: _pv + full GID
        properties[`_pv${item.variantId}`] = item.quantity.toString();
      });

      // Use FormData like the theme does - this allows sections to be returned
      const formData = new FormData();
      formData.append('id', this.bundleVariantId);
      formData.append('quantity', '1');
      
      // Add properties
      Object.entries(properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });

      // Add selling plan if subscription selected
      if (this.isSubscription && this.sellingPlanId) {
        formData.append('selling_plan', this.sellingPlanId);
      }

      // Find all cart-items-component section IDs to request updated HTML
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];
      cartItemsComponents.forEach(item => {
        if (item.dataset.sectionId) {
          sectionIds.push(item.dataset.sectionId);
        }
      });
      
      // Request sections so we get fresh HTML back
      if (sectionIds.length > 0) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      console.log('[Bundle Builder] Add to cart - variant:', this.bundleVariantId, 'sections:', sectionIds);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json'
          }
        });

        const responseData = await response.json();
        
        if (!response.ok || responseData.status) {
          console.error('[Bundle Builder] Cart API error response:', responseData);
          throw new Error(responseData.description || responseData.message || 'Failed to add to cart');
        }

        console.log('[Bundle Builder] Added to cart:', responseData);
        console.log('[Bundle Builder] Sections returned:', responseData.sections ? Object.keys(responseData.sections) : 'none');

        // Dispatch cart:update event with the exact structure the theme expects
        // This matches CartAddEvent from the theme's events.js
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: 'build-a-bundle',
            data: {
              source: 'build-a-bundle',
              itemCount: 1,
              sections: responseData.sections || {}
            }
          }
        }));

        // Fallback: directly open cart drawer if it exists
        setTimeout(() => {
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
            cartDrawer.showDialog();
          } else {
            // Last resort: click the cart button
            const cartButton = document.querySelector('cart-drawer-component button, [href="/cart"]');
            if (cartButton) cartButton.click();
          }
        }, 50);

        // Reset selection after successful add
        this.selectedItems = [];
        this.mobileDrawerOpen = false;

      } catch (e) {
        console.error('[Bundle Builder] Add to cart error:', e);
        alert('Failed to add bundle to cart. Please try again.');
      } finally {
        this.isAddingToCart = false;
      }
    }
  }));
});
